import { Stack } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { column } from './schema';
import { Course, DetailCourse } from 'types/lcms/courses';
import { useDisclosure } from '@mantine/hooks';
import ModalFilterCourse from './ModalFilterCourse';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';
import { useCourseStore } from 'store/createCourseStore';
import ModalDelete from './ModalDelete';
import ModalCheckInOut from './ModalCheckInOut';
import { pathnames } from 'routes/lcms';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { beelajarService } from 'services/beelajar/beelajar.service';
import { useAuth } from 'hooks/useAuth';
import dayjs from 'dayjs';
export interface CourseParams {
  status: string | null;
  startDate: Date | null;
  endDate: Date | null;
  courseCategory: string | null;
  competencyCategory: string | null;
  page?: number;
  size?: number;
}

const TableCourses = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setFormMode, reset } = useCourseStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [opened, { open, close }] = useDisclosure(false);
  const [openedDelete, { open: openModalDelete, close: closeModalDelete }] = useDisclosure(false);
  const [openedCheckIn, { open: openModalCheckIn, close: closeModalCheckIn }] =
    useDisclosure(false);
  const [openedCheckOut, { open: openModalCheckOut, close: closeModalCheckOut }] =
    useDisclosure(false);
  const [selectedRow, setSelectedRow] = useState<Course | null>(null);
  const [detailCourseData, setDetailCourseData] = useState<DetailCourse | null>(null);
  const handleView = (_data: Course) => {
    navigate('/app/lcms/courses/' + _data.msLCMSCourseID);
  };

  const handleEdit = (_data: Course) => {
    reset();
    setFormMode('edit');
    navigate('/app/lcms/courses/edit/' + _data.msLCMSCourseID);
  };

  const handleDelete = (row: Course) => {
    setSelectedRow(row);
    openModalDelete();
  };

  const handleCheckIn = (row: Course) => {
    setSelectedRow(row);
    openModalCheckIn();
  };

  const handleCheckOut = (row: Course) => {
    setSelectedRow(row);
    openModalCheckOut();
  };

  const [columns, setColumns] = useState(
    column(handleView, handleEdit, handleDelete, handleCheckIn, handleCheckOut)
  );

  const search = searchParams.get('search');
  const status = searchParams.get('status');
  const sortBy = searchParams.get('sortBy');
  const sortOrder = searchParams.get('sortOrder');
  const pageNumber = searchParams.get('page');
  const pageSize = searchParams.get('size');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const courseCategory = searchParams.get('courseCategory');
  const competencyCategory = searchParams.get('competencyCategory');

  const {
    data: detailCourse,
    isPending: loadingDetailCourse,
    refetch: refetchDetailCourse
  } = useQuery({
    queryKey: ['lcmsDetail', selectedRow?.msLCMSCourseID],
    queryFn: () => courseService.detail(selectedRow?.msLCMSCourseID as string),
    select: (response) => response.data,
    enabled: Boolean(selectedRow?.msLCMSCourseID)
  });

  useEffect(() => {
    if (selectedRow?.msLCMSCourseID) {
      refetchDetailCourse();
    }
  }, [selectedRow?.msLCMSCourseID]);

  useEffect(() => {
    if (detailCourse?.data) {
      setDetailCourseData(detailCourse?.data as DetailCourse);
    }
  }, [detailCourse]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'lcmsCourses',
      search,
      status,
      sortBy,
      sortOrder,
      pageNumber,
      pageSize,
      startDate,
      endDate,
      courseCategory,
      competencyCategory
    ],
    queryFn: () =>
      courseService.list({
        search: search ?? '',
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD').toString() : '',
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD').toString() : '',
        status: status ?? undefined,
        sortBy: sortBy ?? undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') ?? 'asc',
        pageNumber: Number(pageNumber) || 1,
        pageSize: Number(pageSize) || 10,
        courseCategory: courseCategory?.split('-')[1] ?? undefined,
        competencyCategory: competencyCategory ?? undefined
      })
  });

  const deleteCourse = useMutation({
    mutationFn: courseService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lcmsCourses'] });
      closeModalDelete();
      setSelectedRow(null);
      showSuccessNotification({
        title: 'Success',
        message: 'Course deleted successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error when delete course',
        message: error?.response?.data?.message || 'Failed to delete course'
      });
    }
  });

  const checkInBeelajarLMS = useMutation({
    mutationFn: courseService.checkInBeelajarLMS,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lcmsCourses'] });
      showSuccessNotification({
        title: 'Success',
        message: 'Check in Beelajar LMS successfully'
      });

      closeModalCheckIn();
      setSelectedRow(null);
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error when try start revise course',
        message: error?.response?.data?.message || 'Failed to start revise course'
      });
      setSelectedRow(null);
      closeModalCheckIn();
    }
  });

  const checkOut = useMutation({
    mutationFn: (data: { courseId: string; callback?: () => void }) =>
      courseService.checkOutBeelajarLMS(data.courseId),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lcmsCourses'] });

      // Call the callback after checkout is successful
      if (variables.callback) {
        variables.callback();
      }

      if (profile && detailCourseData) {
        try {
          const beelajarID = detailCourseData?.beelajarID;

          if (beelajarID) {
            showSuccessNotification({
              title: 'Success',
              message: 'To end revise on Beelajar LMS successfully'
            });
          } else {
            setSelectedRow(null);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch course details:', error);
          setSelectedRow(null);
          closeModalDelete();
        }
      } else {
        setSelectedRow(null);
      }
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error when try end revise course',
        message: error?.response?.data?.message || 'Failed to end revise course'
      });
      setSelectedRow(null);
    }
  });

  const beelajarLogin = useMutation({
    mutationFn: beelajarService.loginBeelajarService,
    onSuccess: async (data, variables) => {
      const linkMaterial = detailCourseData?.beelajarID
        ? `${process.env.REACT_APP_BEELAJAR_URL?.replace(
            '/webservice/rest/server.php',
            ''
          )}/course/view.php?id=${detailCourseData?.beelajarID}`
        : '';
      const url = data?.data?.loginurl + '&wantsurl=' + linkMaterial;

      if (variables.isSecondRequest && selectedRow) {
        window.open(url, '_blank');
        await checkInBeelajarLMS.mutateAsync(selectedRow?.msLCMSCourseID);
      }
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error Unauthorized Access',
        message: error.message
      });
      setSelectedRow(null);
      closeModalCheckIn();
    }
  });

  const handleAdd = () => {
    setFormMode('add');
    reset();
    navigate(pathnames.createCourses);
  };

  const onDelete = () => {
    return selectedRow && deleteCourse.mutate(selectedRow?.msLCMSCourseID);
  };

  const onConfirmInOut = (callback: () => void) => {
    if (selectedRow) {
      if (openedCheckIn) {
        // Start with beelajarLogin first, then checkInBeelajarLMS will be called in onSuccess
        if (profile?.adminEmail) {
          beelajarLogin.mutate({ email: profile.adminEmail, isSecondRequest: false });
          setTimeout(() => {
            beelajarLogin.mutate({ email: profile.adminEmail, isSecondRequest: true });
          }, 500);
        }
        return;
      }
      if (openedCheckOut) {
        checkOut.mutate({
          courseId: selectedRow?.msLCMSCourseID as string,
          callback
        });
        closeModalCheckOut();
        setSelectedRow(null);
        return;
      }
    }
  };

  const handleFilter = (data: CourseParams) => {
    close();
    const newParams = new URLSearchParams(searchParams);

    if (data.status) {
      newParams.set('status', data.status.toLowerCase());
    } else {
      newParams.delete('status');
    }

    if (data.startDate) {
      newParams.set('startDate', dayjs(data.startDate).format('YYYY-MM-DD').toString());
    } else {
      newParams.delete('startDate');
    }

    if (data.endDate) {
      newParams.set('endDate', dayjs(data.endDate).format('YYYY-MM-DD').toString());
    } else {
      newParams.delete('endDate');
    }

    if (data.courseCategory) {
      newParams.set('courseCategory', data.courseCategory);
    } else {
      newParams.delete('courseCategory');
    }

    if (data.competencyCategory) {
      newParams.set('competencyCategory', data.competencyCategory);
    } else {
      newParams.delete('competencyCategory');
    }

    setSearchParams(newParams);
  };

  const handleChangeSize = (size: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('size', size.toString());
    setSearchParams(newParams);
  };

  const handleChangePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const handleCloseModalCheckInOut = () => {
    if (openedCheckIn) {
      closeModalCheckIn();
    } else {
      closeModalCheckOut();
    }
    setSelectedRow(null);
  };

  return (
    <Card>
      <Stack>
        <Toolbar
          columns={columns}
          createButtonText='Create Course'
          onCreate={handleAdd}
          onFilter={() => open()}
          showFilterColumn
          setColumns={setColumns}
        />

        <DataTables
          columns={columns}
          data={data?.data || []}
          loading={isLoading}
          totalData={data?.totalRecords}
          currentPage={Number(pageNumber) || 1}
          currentSize={Number(pageSize) || 10}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleChangeSize={handleChangeSize}
          handleChangePage={handleChangePage}
          emptyMessageTitle='No Data Found'
          emptyMessageDescription='Please try again later'
        />
      </Stack>
      <ModalFilterCourse
        defaultValue={{
          status: status ? status?.charAt(0).toUpperCase() + status?.slice(1) : null,
          startDate: startDate ? new Date(startDate) : null,
          endDate: endDate ? new Date(endDate) : null,
          courseCategory: courseCategory || null,
          competencyCategory: competencyCategory || null
        }}
        onClose={() => close()}
        onFilter={handleFilter}
        opened={opened}
      />
      <ModalDelete
        title='Delete Data'
        description={`You are about to delete course ${selectedRow?.courseName}. Are you sure to delete it?`}
        labelConfirm='Delete'
        onClose={() => closeModalDelete()}
        onConfirm={onDelete}
        opened={openedDelete}
        isLoading={deleteCourse.isPending}
      />

      <ModalCheckInOut
        onClose={handleCloseModalCheckInOut}
        onConfirm={onConfirmInOut}
        courseId={selectedRow?.msLCMSCourseID as string}
        opened={openedCheckIn || openedCheckOut}
        type={openedCheckIn ? 'checkin' : 'checkout'}
        isLoading={
          checkInBeelajarLMS.isPending ||
          checkOut.isPending ||
          loadingDetailCourse ||
          beelajarLogin.isPending
        }
      />
    </Card>
  );
};

export default TableCourses;
